import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { api, type Book } from "../api";
import { BackIcon, ChevronIcon } from "../components/icons";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { CardMenu } from "./Home";

/**
 * Lists soft-archived books with restore + permanent-delete actions. Mirrors
 * Home's card shape so the two views feel like one thing. Restore returns the
 * book to the main library; delete is hard and asks for confirmation.
 */
export default function Archive() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [books, setBooks] = useState<Book[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pending, setPending] = useState<Book | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.listBooks("archived").then(setBooks).catch((e) => setErr(String(e)));
  }, []);

  const onRestore = async (book: Book) => {
    setErr(null);
    const prev = books ?? [];
    setBooks(prev.filter((b) => b.id !== book.id));
    try {
      await api.restoreBook(book.id);
    } catch (e) {
      setBooks(prev);
      setErr(String(e));
    }
  };

  const runDelete = async (book: Book) => {
    setErr(null);
    setBusy(true);
    const prev = books ?? [];
    try {
      await api.deleteBook(book.id);
      setBooks(prev.filter((b) => b.id !== book.id));
      setPending(null);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const titleOf = (b: Book) => (b.title.trim() ? `"${b.title}"` : t("archive.thisBook"));

  return (
    <div className="tp-app tp-wide">
      <div className="tp-topbar">
        <button className="tp-topbtn" onClick={() => navigate("/app")} aria-label={t("common.back")}>
          <BackIcon />
        </button>
        <div className="tp-eyebrow">{t("archive.eyebrow")}</div>
        <div style={{ width: 38 }} />
      </div>

      <div className="tp-screen" style={{ paddingTop: 24 }}>
        <div className="tp-display" style={{ marginBottom: 8 }}>{t("archive.title")}</div>
        <div className="tp-body" style={{ color: "var(--tp-ink-soft)", marginBottom: 24, fontSize: 16 }}>
          {t("archive.intro")}
        </div>

        {err && <div className="tp-error">{err}</div>}

        {!books ? (
          <div className="tp-loading">{t("common.loading")}</div>
        ) : books.length === 0 ? (
          <div className="tp-card" style={{ textAlign: "center", padding: 28 }}>
            <div className="tp-h2" style={{ marginBottom: 6 }}>{t("archive.emptyTitle")}</div>
            <div className="tp-body" style={{ color: "var(--tp-ink-soft)", fontSize: 14 }}>
              {t("archive.emptyBody")}
            </div>
          </div>
        ) : (
          <div className="tp-library">
            {books.map((b) => (
              <ArchivedCard
                key={b.id}
                book={b}
                onRestore={() => void onRestore(b)}
                onDelete={() => setPending(b)}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={pending !== null}
        title={t("archive.deleteConfirmTitle")}
        body={
          <Trans
            i18nKey="archive.deleteConfirmBody"
            values={{ title: pending ? titleOf(pending) : t("archive.thisBook") }}
            components={{ strong: <strong style={{ color: "var(--tp-ink)" }} /> }}
          />
        }
        confirmLabel={t("archive.deleteForever")}
        cancelLabel={t("common.cancel")}
        destructive
        busy={busy}
        onConfirm={() => pending && void runDelete(pending)}
        onCancel={() => !busy && setPending(null)}
      />
    </div>
  );
}

function ArchivedCard({
  book,
  onRestore,
  onDelete,
}: {
  book: Book;
  onRestore: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const label = book.title || t("common.untitledBook");
  return (
    <div style={{ position: "relative" }}>
      <Link className="tp-card" to={`/app/books/${book.id}`} style={{
        display: "flex", gap: 14, alignItems: "center", textDecoration: "none", color: "inherit",
        paddingRight: 56, opacity: 0.85,
      }}>
        <div style={{
          width: 70, height: 88, flexShrink: 0,
          borderRadius: 8, overflow: "hidden",
          border: "2px solid var(--tp-ink)",
          background: "var(--tp-surface-alt)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: "var(--tp-font-display)", fontSize: 32,
        }}>📖</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="tp-h2" style={{ marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</div>
          <div className="tp-body" style={{ fontSize: 13, color: "var(--tp-ink-soft)" }}>
            {book.author.name ? t("archive.byAuthor", { name: book.author.name }) : t("archive.yourKidsBook")}
          </div>
          <div className="tp-eyebrow" style={{ fontSize: 10, marginTop: 6, color: "var(--tp-ink-soft)" }}>
            {t("archive.statusArchived")}
          </div>
        </div>
        <ChevronIcon />
      </Link>
      <CardMenu
        items={[
          { label: t("archive.restore"), onSelect: onRestore },
          { label: t("archive.deleteMenu"), onSelect: onDelete, destructive: true },
        ]}
      />
    </div>
  );
}
